/**
 * useNotifications — React hook for fetching and managing in-app notifications.
 *
 * Design: ADR-2 / design §2.6
 *
 * - Fetches from GET /me/notifications; exposes notifications, unreadCount, markRead.
 * - Same security contract as useTracking: token from AuthContext, never stored in state.
 * - Handles NOTIFICATIONS_ENABLED=false gracefully (empty array, no crash).
 * - Shape guard: always extracts from { notifications: [...] } — prevents shape drift.
 * - No PII in logs. No dangerouslySetInnerHTML.
 *
 * Security:
 *   - Authorization header Bearer {idToken}; token refreshed per call.
 *   - notificationId from the Notification object — never from user text input or URL.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import type { Notification } from "@autodieta/shared";
import { useAuth } from "../auth/AuthContext.js";

// ---------------------------------------------------------------------------
// Response shape from GET /me/notifications
// ---------------------------------------------------------------------------

interface NotificationsResponse {
  notifications: Notification[];
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseNotificationsResult {
  /** True while the initial fetch (or a refresh) is in progress. */
  loading: boolean;
  /** Error message if the last fetch failed. Undefined on success. */
  error?: string;
  /** All fetched notifications (may be empty). */
  notifications: Notification[];
  /** Count of notifications with read === false. */
  unreadCount: number;
  /** Mark a notification as read — fires POST /me/notifications/:id/read then refreshes. */
  markRead: (id: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// useNotifications
// ---------------------------------------------------------------------------

/**
 * Fetches the notification list from the worker and provides markRead mutation.
 *
 * @param workerUrl - Base URL of the Cloudflare Worker.
 */
export function useNotifications(workerUrl: string): UseNotificationsResult {
  const { getIdToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const workerUrlRef = useRef(workerUrl);
  workerUrlRef.current = workerUrl;

  const fetchData = useCallback(async () => {
    const token = await getIdToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${workerUrlRef.current}/me/notifications`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.status === 404) {
        // NOTIFICATIONS_ENABLED=false — feature off, degrade gracefully.
        setNotifications([]);
        setError(undefined);
      } else if (!res.ok) {
        setError(`Error ${res.status}: notifications unavailable`);
      } else {
        const raw = (await res.json()) as Record<string, unknown>;
        // Shape guard: extract notifications array safely (ADR-5a pattern).
        const items = Array.isArray(raw["notifications"])
          ? (raw["notifications"] as Notification[])
          : [];
        setNotifications(items);
        setError(undefined);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network error";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const markRead = useCallback(
    async (id: string) => {
      const token = await getIdToken();
      if (!token) return;

      await fetch(`${workerUrlRef.current}/me/notifications/${id}/read`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      // Refresh notifications after mark-read
      setLoading(true);
      await fetchData();
    },
    [getIdToken, fetchData],
  );

  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    loading,
    notifications,
    unreadCount,
    markRead,
    ...(error !== undefined ? { error } : {}),
  };
}
